// ChargeStatusCard.jsx — 1:1급여 정산 탭 맨 위 '진행 상태 카드' (2026-10-06 UX)
//   정산할 달(왜 이 달인지) · 확정 상태(누가·언제) · ① 확인 필요 ② 점검·참고 ③ 정산서 날짜 · 합계 · 확정 버튼
//   hideActions: 휴대폰 — 확정 버튼은 화면 아래 고정 칸에 따로 두고, 확정 상태·달 고르기는 한 줄로 왼쪽부터.
import React, { useState } from 'react';
import MonthNavigator from '../../../components/MonthNavigator';
import { daysInMonth, todayKST, toDateTimeString } from '../../../utils/date';
import { fmtMoney, fmtNum } from './monthlyCalculationUtils';
import { noteCount, stateCounts } from './chargeModel';
import { C } from './charge.styles';

// 확정·확정 취소 시각 — 한국 시간 'YYYY-MM-DD HH:mm'
function whenText(value) {
  return value ? ` · ${toDateTimeString(value).slice(0, 16)}` : '';
}

function stateInfo(confirmation) {
  if (confirmation?.status === 'confirmed') {
    const who = confirmation.confirmedByName ? ` · ${confirmation.confirmedByName}` : '';
    const when = whenText(confirmation.confirmedAt);
    return { tone: 'ok', text: `확정됨${who}${when}` };
  }
  if (confirmation?.status === 'reopened') {
    const who = confirmation.reopenedByName ? ` · ${confirmation.reopenedByName}` : '';
    const when = whenText(confirmation.reopenedAt);
    return { tone: 'warn', text: `확정 취소됨${who}${when} — 고친 뒤 다시 확정해 주세요` };
  }
  return { tone: 'blue', text: '확정 전' };
}

export function ChargeStatusCard({ calc, onCleanup, onRequestConfirm, hideActions = false }) {
  const [showMetrics, setShowMetrics] = useState(false);
  const { year, month } = calc.parsedMonth;
  const counts = stateCounts(calc.rows);
  const notes = noteCount(calc.rows);
  const pending = calc.pendingChargeCount;
  const thisMonth = todayKST().slice(0, 7);
  const isDefault = calc.monthValue === calc.defaultMonthValue;
  const defaultMonthNo = calc.defaultMonthValue ? Number(calc.defaultMonthValue.slice(5, 7)) : null;
  const state = stateInfo(calc.confirmation);
  const t = calc.totals || {};
  const blocked = !calc.rows.length || pending > 0 || calc.loading;

  return (
    <div style={C.status}>
      <div style={C.statusTop}>
        <div style={{ minWidth: 0 }}>
          <div>
            <span style={C.monthTitle}>{year}년 {month}월분 1:1급여</span>
            <span style={C.monthRange}>({month}/1~{month}/{daysInMonth(year, month)})</span>
          </div>
          {isDefault && calc.defaultMonthValue && calc.defaultMonthValue < thisMonth && (
            <div style={C.monthHint}>
              지금은 {Number(thisMonth.slice(5, 7))}월이지만 {month}월분 급여일이 아직 지나지 않아 {month}월분을 정산하고 있어요.
            </div>
          )}
          {!isDefault && defaultMonthNo && (
            <div style={C.monthHint}>지금 정산할 달은 {defaultMonthNo}월분입니다. 지난 달을 보고 있어요.</div>
          )}
        </div>
        <div style={hideActions
          ? { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }
          : { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}
        >
          <span style={C.stateChip(state.tone)}>{state.text}</span>
          <MonthNavigator
            year={year}
            month={month}
            onChange={calc.handleMonthChange}
            onReset={calc.handleDefaultMonth}
            resetLabel="지금 정산할 달로"
            showReset={!isDefault}
            disabled={!calc.defaultMonthReady || calc.loading}
            compact
          />
        </div>
      </div>

      <div style={C.steps}>
        <div style={C.step(pending > 0 ? 'warn' : 'ok')}>
          <span style={C.stepNo}>① 확인 필요</span>
          <span style={C.stepMain}>{pending > 0 ? `${pending.toLocaleString('ko-KR')}건 남음` : '남은 것 없음'}</span>
          <span style={C.stepSub}>자동으로 정할 수 없는 계약이에요. 넣을지 뺄지 정해야 확정할 수 있어요.</span>
          {pending > 0 && !calc.isConfirmed && (
            <button type="button" style={{ ...C.smallLinkBtn, alignSelf: 'flex-start' }} onClick={onCleanup}>정리하기 ›</button>
          )}
        </div>
        <div style={C.step(counts.check > 0 ? 'danger' : null)}>
          <span style={C.stepNo}>② 점검·참고</span>
          <span style={C.stepMain}>점검 {counts.check}명 · 참고 {notes}건</span>
          <span style={C.stepSub}>확정을 막지는 않아요. 월급 미입력처럼 금액이 이상할 수 있는 직원은 '점검'으로 보여요.</span>
        </div>
        <div style={C.step(null)}>
          <span style={C.stepNo}>③ 정산서 날짜</span>
          <input
            type="date"
            style={C.dateInput}
            value={calc.settlementDate}
            onChange={(e) => calc.setSettlementDate(e.target.value)}
            disabled={calc.isConfirmed}
            aria-label="정산서 날짜"
          />
          <span style={C.stepSub}>마케터 정산서 중 이 날짜 정산서에 '1:1급여'로 들어가요(보통 다음 달 1일).</span>
        </div>
      </div>

      <div style={C.totalRow}>
        <span style={C.totalLabel}>1:1급여 합계</span>
        <span style={C.totalValue}>{fmtMoney(t.chargeAmount)}</span>
        <span style={C.totalFormula}>
          {calc.rows.length}명 · 근무일 급여 {fmtMoney(t.proratedSalary)} − 연차 차감 {fmtMoney(t.leaveDeductionAmount)} + 인센티브 {fmtMoney(t.incentiveAmount)}
        </span>
        <button type="button" style={C.smallLinkBtn} onClick={() => setShowMetrics((v) => !v)}>
          {showMetrics ? '내역 접기' : '내역 보기'}
        </button>
      </div>
      {showMetrics && (
        <div style={C.metrics}>
          <div style={C.metric}><div style={C.metricLabel}>근무일 급여</div><div style={C.metricValue}>{fmtMoney(t.proratedSalary)}</div></div>
          <div style={C.metric}><div style={C.metricLabel}>연차 차감</div><div style={C.metricValue}>{fmtMoney(t.leaveDeductionAmount)}</div></div>
          <div style={C.metric}><div style={C.metricLabel}>인센티브</div><div style={C.metricValue}>{fmtMoney(t.incentiveAmount)}</div></div>
          <div style={C.metric}><div style={C.metricLabel}>인정 장수</div><div style={C.metricValue}>{fmtNum(t.weightedCount)}장</div></div>
        </div>
      )}

      {!hideActions && (
        <div style={C.actionRow}>
          <button type="button" style={C.outlineBtn} onClick={() => calc.reload()} disabled={calc.loading} title="다른 메뉴에서 계약·연차가 바뀌었으면 눌러 주세요">
            {calc.loading ? '불러오는 중' : '새로고침'}
          </button>
          {calc.isConfirmed ? (
            <button type="button" style={C.dangerOutlineBtn} onClick={calc.handleReopen} disabled={calc.loading}>확정 취소</button>
          ) : (
            <button type="button" style={C.primaryBtn(blocked)} onClick={onRequestConfirm} disabled={blocked}>
              {month}월분 확정하고 정산서에 넣기
            </button>
          )}
        </div>
      )}
      {!hideActions && !calc.isConfirmed && pending > 0 && (
        <div style={{ ...C.blockReason, textAlign: 'right' }}>확인 필요 {pending}건을 먼저 정리해야 확정할 수 있어요.</div>
      )}
    </div>
  );
}
